import { getState, setState, distinctPlayDays } from './storage';

// A parent should watch their child learn before being asked to pay. Finishing
// the free games in one sitting is not evidence of anything yet, so the upgrade
// screen waits until the child has actually come back across several days.
export const PLAY_DAYS_BEFORE_UPGRADE = 3;

// Cross-session adaptive difficulty: rolling window of last 5 results per game.
export function getGameStats(gameId) {
  const gs = getState().gameStats || {};
  return gs[gameId] || { level: 1, window: [], plays: 0, completed: false };
}

export function getLevel(gameId) {
  return getGameStats(gameId).level;
}

// A game awards one star per round (2), and a third only for a clean run —
// no wrong taps, no hints. Without that third condition the node's 3-star
// display could never fill past 2, which reads as a broken counter.
export function starsForPlay({ rounds, attempts, hints, stars }) {
  const perfect = typeof attempts === 'number' && typeof rounds === 'number'
    && rounds > 0 && attempts <= rounds && !hints;
  return Math.min(3, (stars || 0) + (perfect ? 1 : 0));
}

export function recordGameResult(gameId, { attempts, hints, correct, stars = 0 }) {
  const gs = { ...(getState().gameStats || {}) };
  const s = { ...getGameStats(gameId) };
  const clean = correct && attempts <= 3 && hints === 0;
  const struggled = hints >= 1 || attempts >= 3;
  s.window = [...s.window, { clean, struggled }].slice(-5);
  s.plays += 1;
  if (correct) s.completed = true;
  s.bestStars = Math.max(s.bestStars || 0, stars);
  const last3 = s.window.slice(-3);
  if (last3.length === 3 && last3.every((w) => w.clean) && s.level < 3) {
    s.level += 1;
    s.window = [];
  } else if (s.window.slice(-2).filter((w) => w.struggled).length >= 2 && s.level > 1) {
    s.level -= 1;
    s.window = [];
  }
  gs[gameId] = s;
  setState({ gameStats: gs });
  return s;
}

export function isFreeJourneyComplete(freeGameIds) {
  const gs = getState().gameStats || {};
  return freeGameIds.every((id) => gs[id]?.completed);
}

// The upgrade screen may only appear once the child has finished the free
// journey AND returned across several days. Before that the journey loops back
// into replayable content at a rising level instead of hitting a wall.
export function canShowUpgradeBoundary(freeGameIds) {
  return isFreeJourneyComplete(freeGameIds) && distinctPlayDays() >= PLAY_DAYS_BEFORE_UPGRADE;
}

// Once the child has run out of new games, send them back to whichever finished
// game still has the most room to grow, so the round is genuinely harder rather
// than a repeat. Returns null when nothing has been completed yet.
export function replayCandidate(gameIds) {
  const gs = getState().gameStats || {};
  const done = gameIds.filter((id) => gs[id]?.completed);
  if (!done.length) return null;
  return done.reduce((best, id) => {
    const lv = gs[id]?.level ?? 1;
    const bestLv = gs[best]?.level ?? 1;
    if (lv !== bestLv) return lv < bestLv ? id : best;
    return (gs[id]?.plays ?? 0) < (gs[best]?.plays ?? 0) ? id : best;
  }, done[0]);
}
